import axiosInstance from '@/app/api/axiosInstance';
import { UserProfile } from '@/app/types/auth';
import { PaginatedResponse, ListQueryParams } from '@/app/types';

export interface UserCreate {
  username: string;
  email: string;
  full_name: string;
  password: string;
  role_id: number;
  organization_id: number;
  is_active?: boolean;
}

export interface UserUpdate {
  username?: string;
  email?: string;
  full_name?: string;
  password?: string;
  role_id?: number;
  is_active?: boolean;
}

export interface UserWithSites extends UserProfile {
  sites: Array<{
    id: number;
    name: string;
    code?: string;
    organization_id: number;
  }>;
  managed_parties?: Array<{ // Parties this user can access (multi-party feature)
    id: number;
    name: string;
    phone?: string;
    email?: string;
    billing_address?: string;
  }>;
}

// List all users with optional site filter
export const listUsers = async (params: ListQueryParams & { site_id?: number }): Promise<PaginatedResponse<UserWithSites>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<UserWithSites>>('/users/', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to list users:', error);
    throw error;
  }
};

// Get a specific user by ID
export const getUser = async (userId: string): Promise<UserWithSites> => {
  try {
    const response = await axiosInstance.get<UserWithSites>(`/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to get user with ID ${userId}:`, error);
    throw error;
  }
};

// Create a new user (org_admin only)
export const createUser = async (data: UserCreate): Promise<UserWithSites> => {
  try {
    const response = await axiosInstance.post<UserWithSites>('/users/', data);
    return response.data;
  } catch (error) {
    console.error('Failed to create user:', error);
    throw error;
  }
};

// Update an existing user
export const updateUser = async (userId: string, data: UserUpdate): Promise<UserWithSites> => {
  try {
    const response = await axiosInstance.patch<UserWithSites>(`/users/${userId}`, data);
    return response.data;
  } catch (error) {
    console.error(`Failed to update user with ID ${userId}:`, error);
    throw error;
  }
};

// Delete a user
export const deleteUser = async (userId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/users/${userId}`);
  } catch (error) {
    console.error(`Failed to delete user with ID ${userId}:`, error);
    throw error;
  }
};

// Assign a user to a site
export const assignUserToSite = async (siteId: number, userId: string): Promise<void> => {
  try {
    await axiosInstance.post(`/sites/${siteId}/users/${userId}`);
  } catch (error) {
    console.error(`Failed to assign user ${userId} to site ${siteId}:`, error);
    throw error;
  }
};

// Remove a user from a site
export const removeUserFromSite = async (siteId: number, userId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/sites/${siteId}/users/${userId}`);
  } catch (error) {
    console.error(`Failed to remove user ${userId} from site ${siteId}:`, error);
    throw error;
  }
};

// Get available roles
export interface UserRole {
  id: number;
  name: string;
  permissions?: string[];
}

export const listRoles = async (): Promise<UserRole[]> => {
  // Since there's no roles endpoint, return the known roles from the database
  // In a production app, you'd want to create a proper roles endpoint
  return [
    { id: 1, name: 'superuser' },
    { id: 2, name: 'org_admin' },
    { id: 3, name: 'standard_user' },
    { id: 4, name: 'client' },
    { id: 5, name: 'field_agent' },
    { id: 6, name: 'client_manager' }
  ];
};

// Reset PIN for client user
export interface ResetUserPinResponse {
  user_id: number;
  username: string;
  new_pin: string;
}

export const resetUserPin = async (userId: string): Promise<ResetUserPinResponse> => {
  try {
    const response = await axiosInstance.post<ResetUserPinResponse>(`/users/${userId}/reset-pin`);
    return response.data;
  } catch (error) {
    console.error(`Failed to reset PIN for user ${userId}:`, error);
    throw error;
  }
};